import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  tap,
  map,
  switchMap
} from 'rxjs/operators';
import { AuthActions, AuthApiActions } from './auth.action';
import { CreatePolicyConfiguration } from '../configs/auth.config';
import { AccountInfo } from '@azure/msal-browser';
import { AuthService } from '../services/auth.service';
import { Role } from '../models/role';
import { Group } from '../models/group';

@Injectable()
export class AuthEffects {

  // Login
  login$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.login),
    map(() => {
      this.authService.login();
      return AuthActions.loginProcess();
    })
  ));

  // Init login
  // when user is signed in by selecting account from list of account
  initLoginSucess$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.initLoginSuccess),
    switchMap(() => this.authService.loginSucess().pipe(
      switchMap((account: AccountInfo) => {
        return [
          AuthActions.loginSucess(
            {
              user:
              {
                name: account.name ?? account.username,
                username: account.username,
                roles: (account?.idTokenClaims?.roles ? account.idTokenClaims.roles.map(role => ({ type: role } as Role)) : []),
              }
            }
          ),
          AuthApiActions.loadUserPolicies()
        ];
      })
    ))
  ));

  // Init in progress
  // with automatic login with account from browser storage
  initLoginInProgress$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.initLoginInProgress),
    switchMap(() => this.authService.inProgress().pipe(
      switchMap((account: AccountInfo | null) => {
        if (account) {
          return [
            AuthActions.loginProgress(
              {
                user:
                {
                  name: account.name ?? account.username,
                  username: account.username,
                  roles: (account.idTokenClaims?.roles ? account.idTokenClaims.roles.map(role => ({ type: role } as Role)) : []),
                }
              }
            ),
            AuthApiActions.loadUserPolicies()
          ]
        } else {
          return [AuthActions.loginProgressNoAuth()];
        }
      })
    ))
  ));

  loadUserPolicies$ = createEffect(() => this.actions$.pipe(
    ofType(AuthApiActions.loadUserPolicies),
    switchMap(() => this.authService.getLoginUserRoles().pipe(
      map((groups: Group[]) => AuthApiActions.loadUserPoliciesSuccess({ policies: CreatePolicyConfiguration(groups) }))
    ))
  ));

  // Init logout
  // on logout
  initLogoutSucess$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.initLogoutSuccess),
    switchMap(() => this.authService.logoutSucess().pipe(
      map(() => AuthActions.logoutSuccess())
    ))
  ));

  // Logout
  logout$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.logout),
    tap(() => this.authService.logout())
  ), { dispatch: false });

  // Destroy
  $destroy = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.destroy),
    tap(() => this.authService.destroy())
  ), { dispatch: false });

  constructor(
    private actions$: Actions,
    private authService: AuthService
  ) { }
}